import React, { useEffect, useState } from 'react';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import BookingsIcon from 'assets/Icons/sidebarIcons/BookingsIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import useSavebooking from './crud/useSave';
import { BookingProps } from 'services/hooks/booking-service';
import OccupantService, { OccupantProps } from 'services/hooks/occupant-service';
import InputDatePicker from 'components/elements/Form/Input/inputDatePicker';
import ResourceService, { ResourceProps } from 'services/hooks/resource-service';
import SelectInput from 'components/elements/Form/select/selectInput';
import BookingTimeSelector from './BookingTimeLine/BookingTimeSelector';
import { getformatedStart, getformatedEnd } from 'utils/time';
import { formatedTimeIndex } from 'utils/formatBookingIndex';
import { format, parse } from 'date-fns';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { utcToZonedTime } from 'date-fns-tz';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

// validation
const CreateBookingSchema = yup.object().shape({
  mobileUserUuid: yup.string().required('Occupant is required'),
  reason: yup.string().required('Reason is required'),
  bookingDate: yup.string().required('Date is required'),
  type: yup.string().required('Type is required'),
  resource: yup.string().required('Amenity is required')
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getBookingResult, setGetBookingResult] = useState<BookingProps | null>(null);
  const [resourceResult, setResourceResult] = useState<ResourceProps | null>(null);
  const [occupantResult, setOccupantResult] = useState<OccupantProps | ''>('');
  const [availableBookings, setAvailableBookings] = useState<BookingProps[]>([]);
  const [buildingTimezone, setBuildingTimezone] = useState<any>('');

  const methods = useForm({
    defaultValues:
      getBookingResult && resourceResult
        ? {
            uuid: getBookingResult.uuid,
            start: getBookingResult.start,
            end: getBookingResult.end,
            bookingDate: new Date(getBookingResult.start),
            organizationUuid: getBookingResult.organizationUuid,
            resource: getBookingResult.resource,
            reason: getBookingResult.reason,
            mobileUserUuid: getBookingResult.mobileUserUuid
          }
        : {
            uuid: '',
            start: '',
            end: '',
            reason: '',
            type: '',
            bookingDate: new Date(),
            mobileUserUuid: '',
            organizationUuid: '',
            resource: ''
          },
    resolver: yupResolver(CreateBookingSchema)
  });

  const selectedMobileUuid = methods.watch('mobileUserUuid');
  const selectedResourceUuid = methods.watch('resource');
  const selectedBookingDate = methods.watch('bookingDate');

  const {
    isSuccess,
    data: bookingData,
    refetch: refetchBookingData
  } = useQuery<BookingProps, Error>('booking', () => editQuery(editUUID), {
    enabled: editUUID ? true : false,
    onError: (err: any) => {
      console.log(err.response?.data || err);
    }
  });

  const { data: resourceData } = useQuery<ResourceProps, Error>(
    ['resource', selectedResourceUuid],
    () => ResourceService.findResourceById(selectedResourceUuid),
    {
      enabled: selectedResourceUuid ? true : false,
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  useEffect(() => {
    if (resourceData && selectedBookingDate) {
      setResourceResult(resourceData);
      setBuildingTimezone(resourceData?.building?.timezone);
      if (resourceData.bookings) {
        setAvailableBookings(resourceData.bookings);
      } else {
        /* The below code is setting the state of the `availableBookings` variable to an empty array. This is done when the `resourceData` 
        is not available or when the `selectedBookingDate` is not available. It clears the previous value of `availableBookings`. */
        setAvailableBookings([]);
      }
    } else {
      /* The below code is setting the state of `buildingTimezone` and `availableBookings` to an empty string and an empty array respectively. 
      This is done when the `resourceUuid` is not selected in the form, so it clears the previousvalue. */
      setBuildingTimezone('');
      setAvailableBookings([]);
    }
  }, [resourceData, selectedBookingDate]);

  const { data: occupantData, refetch: refetchOccupantData } = useQuery<OccupantProps, Error>(
    ['occupant', selectedMobileUuid],
    () => OccupantService.findOccupantsById(selectedMobileUuid),
    {
      enabled: selectedMobileUuid ? true : false,
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  useEffect(() => {
    // Refetch the Occupant data when `selectedMobileUuid` changes
    if (selectedMobileUuid) {
      refetchOccupantData();
    }
    // Set state of occupantData
    if (occupantData) {
      setOccupantResult(occupantData);
    } else {
      /* The below code is setting the state of `occupantResult` to an empty string. This is done when the `mobileUserUuid` is not selected 
      in the form, so it clears the previous value of `occupantResult`. */
      setOccupantResult('');
    }
  }, [occupantData, selectedMobileUuid, refetchOccupantData]);

  useEffect(() => {
    if (bookingData && editUUID) {
      refetchBookingData();
      setGetBookingResult(bookingData);
      methods.reset({
        uuid: bookingData.uuid,
        start: bookingData.start,
        end: bookingData.end,
        reason: bookingData.reason,
        type: resourceResult ? resourceResult.type : '',
        bookingDate: utcToZonedTime(bookingData.start, buildingTimezone),
        mobileUserUuid: bookingData.mobileUserUuid,
        organizationUuid: bookingData.organizationUuid,
        resource: bookingData.resource
      });
    } else {
      /* The below code is setting the state of the `getBookingResult` variable to `null`. This is done when there is no booking data available 
      or when the `editUUID` is not provided. It clears the previous value of `getBookingResult`. */
      setGetBookingResult(null);
    }
  }, [bookingData, buildingTimezone, editUUID, methods, refetchBookingData, resourceResult]);

  const saveBookingMutation = useSavebooking({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      if (setEditUUID) setEditUUID('');
    }
  });

  const onSubmit = (e: BookingProps) => {
    saveBookingMutation.mutate({
      uuid: e?.uuid,
      start: getformatedStart(e.bookingDate, e?.start, buildingTimezone),
      end: getformatedEnd(e.bookingDate, e?.end, buildingTimezone),
      reason: e?.reason,
      mobileUserUuid: e?.mobileUserUuid,
      organizationUuid: occupantResult ? occupantResult.organizationUuid : '',
      resource: e?.resource
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Name"
              placeholder="Select Name"
              queryname="occupants"
              name="mobileUserUuid"
              defaultValue={getBookingResult?.mobileUserUuid}
              disabled={editUUID && true}
              errors={methods.formState.errors}
              query={OccupantService.findAllOccupants}
              expectedLabel="FullName"
            />
            <br />
            <Input
              label="Reason"
              name="reason"
              type="text-area"
              // data-testid="example"
              placeholder="A reason for booking..."
              autoFocus
              methods={methods}
            />
            <br />
            <InputDatePicker
              Controller={Controller}
              control={methods.control}
              label="Select Date"
              name="bookingDate"
              placeholder="Select Date"
              minDate={new Date()}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Type"
              name="type"
              defaultValue={resourceResult?.type}
              errors={methods.formState.errors}
              placeholder="Select a type"
              disabled={editUUID ? true : false}
              options={[
                { value: 'ROOM', label: 'Room' },
                { value: 'DESK', label: 'Desk' }
              ]}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Amenity"
              placeholder="Choose an amenity"
              queryname="resources"
              name="resource"
              defaultValue={getBookingResult?.resource}
              disabled={editUUID && true}
              errors={methods.formState.errors}
              query={ResourceService.findAllResources}
              sortBy={methods.watch('type')}
            />
            <Input
              label="Timezone"
              name="timezone"
              type="text-area"
              placeholder="Timezone"
              autoFocus
              methods={methods}
              disabled={true}
              value={buildingTimezone ? buildingTimezone : ''}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            // onClick={onClick}
            iconLeft={<BookingsIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            // disabled={buttonDisabled}
          />
        </form>
        {selectedResourceUuid && resourceResult && (
          <BookingTimeSelector
            bookings={availableBookings}
            buildingTimezone={buildingTimezone}
            selectedResourceUuid={selectedResourceUuid}
            handleStartChange={(start: string) => {
              methods.setValue('start', start);
            }}
            handleEndChange={(end: string) => {
              methods.setValue('end', end);
            }}
            editStart={
              (getBookingResult && formatedTimeIndex(getBookingResult?.start, buildingTimezone)) ||
              0
            }
            editEnd={
              (getBookingResult && formatedTimeIndex(getBookingResult?.end, buildingTimezone)) || 1
            }
            openTime={
              resourceResult && format(parse(resourceResult?.open, 'HH:mm:ss', new Date()), 'p')
            }
            closeTime={
              resourceResult && format(parse(resourceResult?.close, 'HH:mm:ss', new Date()), 'p')
            }
            selectedDate={methods.watch('bookingDate')}
            bookingStartDate={getBookingResult?.start}
            bookingEndDate={getBookingResult?.end}
          />
        )}
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
